import React, { useState, useEffect, useRef } from 'react';
import { View, TextInput, TouchableOpacity, StyleSheet, KeyboardAvoidingView, Platform, Alert, ActivityIndicator, Image, ScrollView, StatusBar, ImageStyle, Dimensions, Modal, FlatList } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { StackScreenProps } from '@react-navigation/stack';
import { AuthStackParamList } from '../../types/navigation';
import { useAuth } from '../../context/AuthContext';
import ThemedText from '../../components/common/ThemedText';
import * as NavigationBar from 'expo-navigation-bar';
import { COUNTRIES, DEFAULT_COUNTRY, Country } from '../../data/countries';
import { Ionicons } from '@expo/vector-icons';

type Props = StackScreenProps<AuthStackParamList, 'PhoneLogin'>;

const AUTH_COLORS = {
  buttonEnabled: '#C4963E',
  buttonDisabled: '#1F1F1F',
  buttonText: '#000000',
  placeholder: '#6B7280',
};

const PhoneLoginScreen: React.FC<Props> = ({ navigation }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const sendingRef = useRef(false);
  const { sendOTP } = useAuth();
  const insets = useSafeAreaInsets();
  const [dimensions, setDimensions] = useState(Dimensions.get('window'));

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setDimensions(window);
    });
    
    return () => subscription?.remove();
  }, []);

  const isMobileWeb = Platform.OS === 'web' && dimensions.width < 768;

  useEffect(() => {
    if (Platform.OS === 'android') {
      NavigationBar.setButtonStyleAsync('light').catch(() => {});
    }
  }, []);

  const validatePhoneNumber = (phone: string): boolean => {
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    // Check if it matches the selected country's phone length
    return cleaned.length === selectedCountry.maxLength;
  };

  const handleSendOTP = async () => {
    if (!validatePhoneNumber(phoneNumber)) {
      Alert.alert('Invalid Phone Number', `Please enter a valid ${selectedCountry.maxLength}-digit phone number for ${selectedCountry.name}.`);
      return;
    }

    if (sendingRef.current) {
      return;
    }

    sendingRef.current = true;
    setLoading(true);
    try {
      // Clean phone number and add country code
      const cleanedPhone = phoneNumber.replace(/\D/g, '');
      const phoneWithCountryCode = `${selectedCountry.code}${cleanedPhone}`;
      await sendOTP({ phoneNumber: phoneWithCountryCode });
      
      // Navigate to OTP verification screen with full phone number including country code
      navigation.navigate('OTPVerification', { phoneNumber: phoneWithCountryCode });
    } catch (error) {
      Alert.alert('Error', error instanceof Error ? error.message : 'Failed to send OTP. Please try again.');
    } finally {
      sendingRef.current = false;
      setLoading(false);
    }
  };

  const formatPhoneNumber = (text: string) => {
    // Remove all non-digit characters
    const cleaned = text.replace(/\D/g, '');
    // Limit to selected country's max length
    const limited = cleaned.slice(0, selectedCountry.maxLength);
    setPhoneNumber(limited);
  };

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setShowCountryPicker(false);
    // Clear phone number when country changes
    setPhoneNumber('');
  };

  const renderCountryItem = ({ item }: { item: Country }) => (
    <TouchableOpacity
      style={styles.countryItem}
      onPress={() => handleCountrySelect(item)}
    >
      <ThemedText style={styles.countryFlag}>{item.flag}</ThemedText>
      <View style={styles.countryInfo}>
        <ThemedText style={styles.countryName}>{item.name}</ThemedText>
        <ThemedText style={styles.countryCodeInList}>{item.code}</ThemedText>
      </View>
      {selectedCountry.code === item.code && selectedCountry.countryCode === item.countryCode && (
        <Ionicons name="checkmark" size={24} color="#C4963E" />
      )}
    </TouchableOpacity>
  );

  const isButtonDisabled = !validatePhoneNumber(phoneNumber) || loading;

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <StatusBar barStyle="light-content" backgroundColor="#000000" translucent={false} />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <ScrollView
          contentContainerStyle={styles.content}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          {/* Logo/Header */}
          <View style={styles.header}>
            <Image
              source={require('../../../assets/logo-white.png')}
              style={styles.logoImage}
              resizeMode="contain"
            />
            <ThemedText style={styles.welcomeText}>Welcome to Leads n Business</ThemedText>
          </View>

          {/* Form */}
          <View style={styles.form}>
            <ThemedText style={styles.inputLabel}>Phone Number</ThemedText>
            
            <View style={styles.inputContainer}>
              <TouchableOpacity
                style={[styles.countryCode, isMobileWeb && styles.countryCodeMobile]}
                onPress={() => setShowCountryPicker(true)}
                disabled={loading}
              >
                <ThemedText style={styles.countryFlag}>{selectedCountry.flag}</ThemedText>
                <ThemedText style={styles.countryCodeText}>{selectedCountry.code}</ThemedText>
                <Ionicons name="chevron-down" size={16} color="#FFFFFF" style={styles.chevronIcon} />
              </TouchableOpacity>
              <TextInput
                style={[styles.input, isMobileWeb && styles.inputMobile]}
                placeholder="Phone number"
                placeholderTextColor={AUTH_COLORS.placeholder}
                value={phoneNumber}
                onChangeText={formatPhoneNumber}
                keyboardType="phone-pad"
                maxLength={selectedCountry.maxLength}
                autoFocus
                editable={!loading}
              />
            </View>

            <TouchableOpacity
              style={[styles.button, { backgroundColor: isButtonDisabled ? AUTH_COLORS.buttonDisabled : AUTH_COLORS.buttonEnabled }]}
              onPress={handleSendOTP}
              disabled={isButtonDisabled}
            >
              {loading ? (
                <ActivityIndicator color={AUTH_COLORS.buttonText} />
              ) : (
                <ThemedText style={[styles.buttonText, { color: AUTH_COLORS.buttonText }]}>Send OTP</ThemedText>
              )}
            </TouchableOpacity>
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <ThemedText style={styles.footerText}>
              By continuing, you agree to LNB Terms of Service and Privacy Policy
            </ThemedText>
          </View>

          {/* reCAPTCHA lives in web/index.html (#recaptcha-container) to avoid duplicate IDs */}
        </ScrollView>
      </KeyboardAvoidingView>

      {/* Country Picker Modal */}
      <Modal
        visible={showCountryPicker}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowCountryPicker(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContainer, { paddingBottom: Math.max(insets.bottom, 16) }]}>
            <View style={styles.modalHeader}>
              <ThemedText style={styles.modalTitle}>Select Country</ThemedText>
              <TouchableOpacity
                onPress={() => setShowCountryPicker(false)}
                style={styles.closeButton}
              >
                <Ionicons name="close" size={28} color="#FFFFFF" />
              </TouchableOpacity>
            </View>
            <FlatList
              data={COUNTRIES}
              keyExtractor={(item) => `${item.code}-${item.countryCode}`}
              renderItem={renderCountryItem}
              contentContainerStyle={styles.countryList}
              showsVerticalScrollIndicator={false}
            />
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000000',
  },
  keyboardView: {
    flex: 1,
    ...(Platform.OS === 'web' ? {
      maxWidth: 480,
      width: '100%',
      marginHorizontal: 'auto' as any,
    } : {}),
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 32,
    justifyContent: 'space-between',
  },
  header: {
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 32,
  },
  logoImage: {
    width: 96,
    height: 96,
    marginBottom: 24,
  } as ImageStyle,
  welcomeText: {
    fontSize: 24,
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  form: {
    width: '100%',
    justifyContent: 'center',
    marginBottom: 32,
  },
  inputLabel: {
    color: '#9CA3AF',
    fontSize: 13,
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 12,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
    width: '100%',
    maxWidth: '100%',
  },
  countryCode: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    height: 56,
    borderWidth: 1,
    borderRadius: 12,
    marginRight: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderColor: 'rgba(255, 255, 255, 0.2)',
    flexShrink: 0,
  },
  countryCodeText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  countryFlag: {
    fontSize: 24,
    marginRight: 8,
  },
  chevronIcon: {
    marginLeft: 4,
  },
  input: {
    flex: 1,
    height: 56,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 0,
    fontSize: 18,
    color: '#FFFFFF',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderColor: 'rgba(255, 255, 255, 0.2)',
    minWidth: 0,
    ...(Platform.OS === 'web' ? {
      outline: 'none',
      boxSizing: 'border-box',
    } : {}),
  },
  countryCodeMobile: {
    paddingHorizontal: 8,
    marginRight: 8,
  },
  inputMobile: {
    minWidth: 0,
    paddingHorizontal: 12,
  },
  button: {
    height: 56,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  footer: {
    paddingBottom: 24,
  },
  footerText: {
    fontSize: 12,
    textAlign: 'center',
    lineHeight: 18,
    color: '#6B7280',
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContainer: {
    backgroundColor: '#1F1F1F',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  closeButton: {
    padding: 4,
  },
  countryList: {
    paddingVertical: 8,
  },
  countryItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.05)',
  },
  countryInfo: {
    flex: 1,
    marginLeft: 12,
  },
  countryName: {
    fontSize: 16,
    fontWeight: '500',
    color: '#FFFFFF',
    marginBottom: 2,
  },
  countryCodeInList: {
    fontSize: 14,
    color: '#9CA3AF',
  },
});

export default PhoneLoginScreen;
